<template>
  <section class="base-container-first page">
    <SharedPageHeader class="page__header">
      <span v-html="t('pages.client.profile.title')" />
    </SharedPageHeader>

    <BaseForm
      class="base-content-block content__wrapper"
      @submit="(event) => updateClientProfileHandleSubmit(event)"
    >
      <h3
        class="typography__headline4"
        v-html="t('pages.client.profile.basicInfo.title')"
      />

      <div class="form__basic">
        <div class="form__container">
          <div class="content__form">
            <div class="content-form__inputs">
              <BaseInput2
                v-model="updateClientProfileFields.name[0].value"
                v-bind="updateClientProfileFields.name[1].value"
                class="base-input-first"
                :error="updateClientProfileErrors.name"
                :label="t('pages.client.profile.basicInfo.inputs.name.label')"
                name="name"
                :placeholder="t('pages.client.profile.basicInfo.inputs.name.placeholder')"
              />

              <BaseInput2
                v-model="updateClientProfileFields.surname[0].value"
                v-bind="updateClientProfileFields.surname[1].value"
                class="base-input-first"
                :error="updateClientProfileErrors.surname"
                :label="t('pages.client.profile.basicInfo.inputs.surname.label')"
                name="surname"
                :placeholder="t('pages.client.profile.basicInfo.inputs.surname.placeholder')"
              />
            </div>

            <div class="content-form__inputs">
              <BasePhoneInput
                v-model="updateClientProfileFields.phone[0].value"
                v-bind="updateClientProfileFields.phone[1].value"
                class="base-input-first"
                :error="updateClientProfileErrors.phone"
                :label="t('pages.client.profile.basicInfo.inputs.phone.label')"
                name="phone"
                :placeholder="t('pages.client.profile.basicInfo.inputs.phone.placeholder')"
              />

              <BaseSelect2
                v-if="languageList"
                v-model="updateClientProfileFields.language_id[0].value"
                v-bind="updateClientProfileFields.language_id[1].value"
                class="base-select-first"
                :error="updateClientProfileErrors.language_id"
                :label="t('pages.client.profile.basicInfo.inputs.language.label')"
                name="language_id"
                :options="languageList.map((language) => ({ name: language.name, value: language.id }))"
                :placeholder="t('pages.client.profile.basicInfo.inputs.language.placeholder')"
                searchable
              />
            </div>

            <BaseSelect2
              v-if="countryList"
              key="base-select2-2"
              v-model="updateClientProfileFields.country_id[0].value"
              v-bind="updateClientProfileFields.country_id[1].value"
              class="base-select-first"
              :error="updateClientProfileErrors.country_id"
              :label="t('pages.client.profile.basicInfo.inputs.country.label')"
              name="country_id"
              :options="countryList.map((country) => ({ name: country.name, value: country.id }))"
              :placeholder="t('pages.client.profile.basicInfo.inputs.country.placeholder')"
              searchable
            />

            <BaseSelect2
              v-if="timezoneList"
              key="base-select2-3"
              v-model="updateClientProfileFields.timezone[0].value"
              v-bind="updateClientProfileFields.timezone[1].value"
              class="base-select-first"
              :error="updateClientProfileErrors.timezone"
              :label="t('pages.client.profile.basicInfo.inputs.timezone.label')"
              name="timezone"
              :options="timezoneList.map((timezone) => ({ name: timezone.name, value: timezone.id }))"
              :placeholder="t('pages.client.profile.basicInfo.inputs.timezone.placeholder')"
              searchable
            />

            <BaseSelect2
              v-if="projectList"
              key="base-select2-4"
              v-model="updateClientProfileFields.priority_project_id[0].value"
              v-bind="updateClientProfileFields.priority_project_id[1].value"
              class="base-select-first"
              :error="updateClientProfileErrors.priority_project_id"
              :label="t('pages.client.profile.basicInfo.inputs.project.label')"
              name="priority_project_id"
              :options="[
                { name: t('pages.client.profile.basicInfo.inputs.project.all'), value: null },
                ...projectList.map((project) => ({ name: project.name, value: project.id })),
              ]"
              :hint="t('pages.client.profile.basicInfo.inputs.project.placeholder')"
              searchable
            />
          </div>
        </div>

        <div class="form__container">
          <BaseInputFile2
            v-model="updateClientProfileFields.form_avatar_files[0].value"
            v-bind="updateClientProfileFields.form_avatar_files[1].value"
            accept=".jpeg,.jpg,.png"
            class="base-input-file-first"
            class-loader="base-loader-first"
            :delete-button-text="t('pages.client.profile.basicInfo.inputs.avatar.deleteButtonText')"
            :error="updateClientProfileErrors.form_avatar_files"
            :hint="t('pages.client.profile.basicInfo.inputs.avatar.hint')"
            :label="t('pages.client.profile.basicInfo.inputs.avatar.label')"
            :loading="updateClientProfileLoadingAvatar"
            name="form_avatar_files"
            :placeholder="t('pages.client.profile.basicInfo.inputs.avatar.placeholder')"
            @delete="onAvatarDelete"
            :enable-cropping="true"
            :crop-auto-open="true"
            :crop-aspect-ratio="1"
            :crop-output-options="{ width: 1000, height: 1000, quality: 0.9 }"
          />
        </div>
      </div>

      <BaseButton2
        class="base-button-big base-button-first content__button"
        class-loader="base-loader-second"
        :disabled="!updateClientProfileMeta.touched"
        :loading="updateClientProfileLoading"
        type="submit"
      >
        <span v-html="t('pages.client.profile.submitButton')" />
      </BaseButton2>
    </BaseForm>

    <div class="base-content-block content__wrapper">
      <h3
        class="typography__headline4"
        v-html="t('pages.client.profile.entrySection.header')"
      />

      <div class="form__basic">
        <div class="content__form">
          <BaseInput2
            class="base-input-first"
            disabled
            :error="!client.is_confirm_email"
            :model-value="client.email"
            name="email"
            type="email"
          >
            <template #label="{ id }">
              <label
                class="typography__label1"
                :for="id"
              >
                {{ t("pages.client.profile.entrySection.inputs.email.label") }} <span class="form__text--gray">*</span>
              </label>
            </template>

            <template
              v-if="client.is_confirm_email"
              #suffix
            >
              <BaseIcon2
                id="icon-check"
                class="form__icon-check"
              />
            </template>

            <template #error>
              <p class="form__error typography__label1">
                {{ t("pages.client.profile.entrySection.confirm") }}

                <BaseButton2
                  class="ui-link-secondary"
                  :disabled="sendEmailConfirmLoading"
                  @click="() => sendNewConfirmEmail()"
                >
                  {{ t("pages.client.profile.entrySection.confirmNewLink") }}
                </BaseButton2>
              </p>
            </template>
          </BaseInput2>

          <BaseForm
            class="form__container"
            @submit="(event) => changeClientPasswordHandleSubmit(event)"
          >
            <div class="content__form">
              <div class="content-form__inputs-wrapper">
                <p class="typography__label1">
                  {{
                    client.need_set_password
                      ? t("pages.client.profile.passwordSection.titleNeedPassword")
                      : t("pages.client.profile.passwordSection.titleHasPassword")
                  }}
                </p>

                <div class="content-form__inputs content-form__inputs-password">
                  <!-- TODO: Заменить на BaseInputPassword -->
                  <BaseInput2
                    v-if="!client.need_set_password"
                    v-model="changeClientPasswordFields.old_password[0].value"
                    v-bind="changeClientPasswordFields.old_password[1].value"
                    autocomplete="password"
                    class="base-input-first"
                    :error="changeClientPasswordErrors.old_password"
                    name="old_password"
                    :placeholder="t('pages.client.profile.passwordSection.inputs.currentPassword.placeholder')"
                    type="password"
                  />

                  <!-- TODO: Заменить на BaseInputPassword -->
                  <BaseInput2
                    v-model="changeClientPasswordFields.new_password[0].value"
                    v-bind="changeClientPasswordFields.new_password[1].value"
                    autocomplete="password"
                    class="base-input-first"
                    :error="changeClientPasswordErrors.new_password"
                    name="new_password"
                    :placeholder="t('pages.client.profile.passwordSection.inputs.newPassword.placeholder')"
                    type="password"
                  />

                  <!-- TODO: Заменить на BaseInputPassword -->
                  <BaseInput2
                    v-model="changeClientPasswordFields.new_password_repeat[0].value"
                    v-bind="changeClientPasswordFields.new_password_repeat[1].value"
                    autocomplete="password"
                    class="base-input-first"
                    :error="changeClientPasswordErrors.new_password_repeat"
                    name="new_password_repeat"
                    :placeholder="t('pages.client.profile.passwordSection.inputs.passwordConfirm.placeholder')"
                    type="password"
                  />
                </div>
              </div>
            </div>

            <BaseButton2
              class="base-button-big base-button-first content__button"
              class-loader="base-loader-second"
              :disabled="!changeClientPasswordMeta.touched"
              :loading="changeClientPasswordLoading"
              type="submit"
            >
              <span v-html="t('pages.client.profile.submitButton')" />
            </BaseButton2>
          </BaseForm>
        </div>
      </div>
    </div>

    <div class="base-content-block content__wrapper">
      <h3
        class="typography__headline4"
        v-html="t('pages.client.profile.social.title')"
      />

      <div class="form__basic">
        <SharedAuthSocialButtons :columns="2">
          <SharedSocialConnectButton
            :connected="client.is_vk_connected"
            :params="{ token: tokenState.accessToken }"
            project="crm"
            :redirect-to="url.href"
            social="vk"
          />

          <SharedSocialConnectButton
            :connected="client.is_yandex_connected"
            :params="{ token: tokenState.accessToken }"
            project="crm"
            :redirect-to="url.href"
            social="yandex"
          />

          <SharedSocialConnectButton
            :connected="client.is_google_connected"
            :params="{ token: tokenState.accessToken }"
            project="crm"
            :redirect-to="url.href"
            social="google"
          />

          <SharedSocialConnectButton
            :connected="client.is_mailru_connected"
            :params="{ token: tokenState.accessToken }"
            project="crm"
            :redirect-to="url.href"
            social="mailru"
          />
        </SharedAuthSocialButtons>
      </div>
    </div>

    <div class="base-content-block content__wrapper">
      <h3
        class="typography__headline4"
        v-html="t('pages.client.profile.delete.title')"
      />

      <p v-html="t('pages.client.profile.delete.description')" />

      <BaseModalButton
        class="base-button-big base-button-second content__button"
        :disabled="updateClientProfileLoading || changeClientPasswordLoading"
        :modal="MODAL.ClientProfileDelete"
      >
        <span v-html="t('pages.client.profile.delete.button')" />
      </BaseModalButton>
    </div>

    <LazyProfileDeleteModal
      v-if="isModalOpen(MODAL.ClientProfileDelete)"
      :id="MODAL.ClientProfileDelete"
    />

    <LazySharedUpdateWarningModal
      v-if="isModalOpen(SHARED_MODAL.UpdateWarning)"
      @success="() => navigateToRoute()"
    />
  </section>
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { tokenState } = useAuthStore();
const { client } = useClientStore();
const { isModalOpen } = useModalStore();
const { openNotification } = useNotificationStore();
const url = useRequestURL();

const [{ list: countryList }, { list: languageList }, { list: timezoneList }, { list: projectList }] =
  await Promise.all([
    useGetCountryListDto({
      order_by: [
        {
          field: "name",
          sort: "ASC",
        },
      ],
    }),
    useGetLanguageListDto({
      order_by: [
        {
          field: "name",
          sort: "ASC",
        },
      ],
    }),
    useGetTimezoneListDto(),
    useGetProjectListDto({
      limit: 100, // TODO: Добавить пагинацию
      order_by: [
        {
          field: "id",
          sort: "DESC",
        },
      ],
    }),
    {
      fetchPolicy: "no-cache",
    },
  ]);

const { loading: sendEmailConfirmLoading, sendEmailConfirm } = useAuthSendEmailConfirm();

const {
  errors: updateClientProfileErrors,
  fields: updateClientProfileFields,
  handleSubmit: updateClientProfileHandleSubmit,
  isSubmitting: updateClientProfileIsSubmitting,
  loading: updateClientProfileLoading,
  loadingAvatar: updateClientProfileLoadingAvatar,
  meta: updateClientProfileMeta,
  setFieldTouched: updateClientProfileSetFieldTouched,
} = useUpdateClientProfileForm();

// Отслеживаем изменения в поле avatar для установки touched
watch(
  () => updateClientProfileFields.form_avatar_files[0].value,
  (newValue, oldValue) => {
    // Если файл был добавлен или изменен, устанавливаем touched
    if (newValue && newValue.length > 0) {
      updateClientProfileSetFieldTouched("form_avatar_files", true);
    }
    // Если файл был удален и раньше был, тоже устанавливаем touched
    else if (!newValue && oldValue && oldValue.length > 0) {
      updateClientProfileSetFieldTouched("form_avatar_files", true);
    }
  },
  { deep: true },
);

const {
  errors: changeClientPasswordErrors,
  fields: changeClientPasswordFields,
  handleSubmit: changeClientPasswordHandleSubmit,
  loading: changeClientPasswordLoading,
  meta: changeClientPasswordMeta,
} = useChangeClientPasswordForm();

const { navigateToRoute } = useUpdateWarning(updateClientProfileMeta, updateClientProfileIsSubmitting);

const sendNewConfirmEmail = async () => {
  await sendEmailConfirm();
  openNotification(t("pages.client.profile.notifications.successSubmit"));
};

const onAvatarDelete = () => {
  if (client.value.avatar) {
    updateClientProfileSetFieldTouched("form_avatar_files", true);
  } else {
    updateClientProfileSetFieldTouched("form_avatar_files", false);
  }
};

useSeoMeta({
  title: t("pages.client.profile.seo.title"),
});

definePageMeta({
  layout: "cabinet",
});
</script>

<style scoped>
:deep(.data__body) {
  flex-direction: column-reverse;
}

.page {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.content__wrapper {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
}

@media (--desktop) {
  .content__wrapper {
    row-gap: 24px;
  }
}

.form__container,
.form__basic {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
}

@media (--desktop) {
  .form__basic {
    display: grid;
    grid-template-columns: 2fr 1fr;
    column-gap: 32px;
  }
}

.content__form {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  max-width: 770px;
}

.content-form__inputs {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (--desktop) {
  .content-form__inputs {
    flex-direction: row;
  }
}

.content-form__inputs-wrapper {
  display: flex;
  flex-direction: column;
  row-gap: 8px;
}

.content-form__inputs-password {
  gap: 8px;
}

@media (--desktop) {
  .content__button {
    width: min-content;
  }
}

.form__text--gray {
  color: var(--grey-3);
}

.form__icon-check {
  width: 16px;
  height: 16px;
  color: var(--orange-1);
}

.form__error {
  color: var(--red);
}
</style>
